/*----------------------------------
- LANDING EXPERIENCE
----------------------------------*/

import React from 'react';

import useContext from '@/client/context';
import type { IconName } from '@uniquedomains/components/ui/icon';
import GlossaryText, { GlossarySection } from '@/client/components/glossary/GlossaryText';
import Icon from '@/client/components/ui/icon/Icon';
import WebsiteSupportingLink from '@uniquedomains/components/website/WebsiteSupportingLink';
import HomeSocialProof from '@/client/pages/landing/components/sections/product/HomeSocialProof';
import HomeDomainsPreview from '@/client/pages/landing/components/sections/product/HomeDomainsPreview';
import {
    HOME_FINAL_CTA_COPY,
    HOME_FINAL_CTA_PERSONA_CARDS,
    LANDING_PERSONAS,
} from '@/client/catalogs/landing/pageContent';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import HomeWorkflowBento from '@/client/pages/landing/components/sections/product/HomeWorkflowBento';
import HomePlatformSection from '@/client/pages/landing/components/sections/product/HomePlatformSection';
import { FAQ, FAQ_SECTION_CLASS } from '@/client/pages/landing/components/sections/faq/FAQ';
import {
    PAGE_CONTAINER_CLASS,
    SNAP_ITEM_CLASS,
    SNAP_ROW_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import { HOME_FAQ_ITEMS } from '@/client/catalogs/landing/faq';
import HomeAskAiSection from '@/client/pages/landing/components/ask-ai/HomeAskAiSection';
import PersonaAuthCtaPair from '@/client/pages/landing/components/conversion/PersonaAuthCtaPair';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildHomeGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import {
    VISIBLE_CONTEXTUAL_SEARCH_PERSONAS,
    type ContextualSearchVisiblePersonaIntent,
} from '@uniquedomains/components/contracts/upgrade/catalogs/contextualSearchConversion';
import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import type { InitialData } from '@/client/pages/landing/types';
import { COMPARE_BASE_PATH, comparePagePath } from '@/common/catalogs/landing/comparePages';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { WEBSITE_DOMAINS_BASE_PATH, encodeRadarFiltersUrl } from '@/common/libs/filters';
import { toAbsoluteProductUrl } from '@/client/utils/payment/productUrls';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { formatCompactStat, formatMillionsStat } from '@/client/pages/landing/lib/formatCompactStat';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import { isPublishedHumanPageTarget } from '@/common/catalogs/landing/internalLinks';
import { START_WITH_BRIEF_LABEL } from '@/client/catalogs/landing/accessOffer';

type LandingExperienceProps = {
    initialData: InitialData;
};


const FINAL_PERSONA_TRIGGER_IDS: Record<ContextualSearchVisiblePersonaIntent, ConversionTriggerId> = {
    investor: 'website_landing_final_investor',
    founder: 'website_landing_final_founder',
};
type LandingCardVisual = {
    iconName: IconName;
    accent: string;
    accentSoft: string;
    glow: string;
};
type LandingToolLogo = {
    src: string;
    width: number;
    height: number;
    viewBox: string;
    assetWidth: number;
    assetHeight: number;
};
type LandingToolRef = {
    label: string;
    /** Slug of the published /compare/:slug page, when one exists for this tool. */
    compareSlug?: string;
    /** Omitted when the available asset does not read well on the light card. */
    logo?: LandingToolLogo;
};
type LandingCopyCard = LandingCardVisual & {
    title: string;
    tools: readonly LandingToolRef[];
    greatText: string;
    weakText: string;
};
const DIFFERENTIATION_LOGO_SCALE = 1.5;

/**
 * The desktop hero's fluid vertical rhythm, unchanged from the shipped Home v2.
 *
 * It only applies from `lg` up: the phone mockup sets the hero 28px under the top
 * bar and closes it on the figure below, so the fluid pair would only fight it.
 */
const HERO_SECTION_DESKTOP_PADDING_CLASS =
    'lg:pt-[clamp(2.5rem,1.8rem+2.6vw,4.5rem)] lg:pb-[clamp(0.417rem,0.317rem+0.333vw,0.667rem)]';

/* The three figures under the hero. Phone: a tinted tile in a three-up grid.
   From `lg`: the bare centred column the shipped desktop draws, including the
   link's own -8/-14 hover box, which only the linked figures paint. */
const HERO_STAT_TILE_CLASS = [
    'flex min-w-0 flex-col gap-2 rounded-[18px] bg-white/[0.72] px-3 py-3.5 text-[#1b1d1e]',
    'lg:-mx-3.5 lg:-my-2 lg:min-w-28 lg:items-center lg:gap-[0.35rem] lg:rounded-[18px] lg:bg-transparent',
    'lg:px-3.5 lg:py-2 lg:text-center',
].join(' ');
const HERO_STAT_LINK_CLASS = [
    HERO_STAT_TILE_CLASS,
    'transition-colors duration-[140ms] lg:hover:bg-[#ffffffb3] lg:focus-visible:bg-[#ffffffb3]',
].join(' ');
const HERO_STAT_VALUE_CLASS =
    'text-[30px] font-light leading-[0.95] tracking-[-0.045em] tabular-nums lg:text-[3.4rem]';
const HERO_STAT_LABEL_CLASS = [
    'inline-flex flex-wrap items-center gap-1 text-[10px] font-bold uppercase leading-[1.35]',
    'tracking-[0.14em] text-[#66696b]',
    // 1.5 is the inherited unitless line height the shipped label resolved against
    // its own 0.68rem, so restoring it keeps the desktop band the height it ships.
    'lg:flex-nowrap lg:gap-1.5 lg:whitespace-nowrap lg:text-[0.68rem] lg:leading-[1.5] lg:tracking-[0.18em]',
].join(' ');

/* "Most tools help you look." cards. A white bordered card in the phone's snap
   row, the shipped `ud-landing-value-card` geometry from `lg` (radius 1.5rem,
   #f8f7f5, 1.75rem/1.625rem padding). Those rules live unlayered in shared.css,
   which no utility can override, so the card carries neither class now. */
const TOOL_LANDSCAPE_CARD_CLASS = [
    'flex min-w-0 flex-col rounded-[22px] border border-[#ebe7de] bg-white px-[18px] py-5',
    'lg:block lg:rounded-3xl lg:border-0 lg:bg-[#f8f7f5] lg:px-[1.625rem] lg:py-7',
].join(' ');
/* Wraps inside the 300px phone card, stays on one line in the wider desktop cell. */
const TOOL_LANDSCAPE_LOGO_ROW_CLASS = [
    'mb-3.5 flex min-h-[22px] flex-wrap items-center gap-x-3.5 gap-y-2.5',
    'lg:mb-[1.2rem] lg:min-h-8 lg:flex-nowrap lg:gap-y-0',
].join(' ');
// "Most tools help you look." card catalog. Order: the one-word list and
// investor-data cards lead, then the generic looking tools.
// Every weakText derives from a fact-checked verdictSummary bullet in
// client/catalogs/landing/compare/content/<slug>.ts so the homepage never
// claims more than the matching /compare page concedes.
const DIFFERENTIATION_CARDS: readonly LandingCopyCard[] = [
    {
        // weakText source: content/expireddomains-net.ts ("A firehose of rows with no evaluation").
        // mononames renders as a text mark: its wordmark asset is light gray on light cards.
        // oneword.domains renders as a text mark: see note on its tools entry.
        title: 'One-word lists & expired names',
        tools: [
            // Text mark: the square icon asset turns into a dense blob under the mono filter.
            { label: 'oneword.domains', compareSlug: 'one-word-domains' },
            {
                label: 'ExpiredDomains.net',
                compareSlug: 'expireddomains-net',
                logo: {
                    src: '/public/assets/compare/expireddomains-net/logo.webp',
                    width: 104,
                    height: 15,
                    viewBox: '0 0 430 62',
                    assetWidth: 430,
                    assetHeight: 62,
                },
            },
            { label: 'mononames', compareSlug: 'mononames' },
        ],
        greatText: 'Huge lists of available names to browse.',
        weakText: 'Thousands of rows, no evaluation. The triage is still your job.',
        iconName: 'columns',
        accent: '#6b21a8',
        accentSoft: '#f3e8ff',
        glow: 'rgba(168, 85, 247, 0.2)',
    },
    {
        // weakText source: content/estibot.ts ("A dollar number is not a decision about whether to acquire at all").
        // Bishopi renders as a text mark: its only asset is a white-on-dark variant.
        title: 'Appraisals & investor data',
        tools: [
            {
                label: 'Estibot',
                compareSlug: 'estibot',
                logo: {
                    src: '/public/assets/compare/estibot/logo.webp',
                    width: 57,
                    height: 19,
                    viewBox: '0 0 580 194',
                    assetWidth: 580,
                    assetHeight: 194,
                },
            },
            {
                label: 'NameBio',
                compareSlug: 'namebio',
                logo: {
                    src: '/public/assets/compare/namebio/logo.png',
                    width: 97,
                    height: 16,
                    viewBox: '0 0 181 30',
                    assetWidth: 181,
                    assetHeight: 30,
                },
            },
            {
                label: 'dotDB',
                compareSlug: 'dotdb',
                logo: {
                    src: '/public/assets/compare/dotdb/logo.webp',
                    width: 59,
                    height: 16,
                    viewBox: '0 0 1000 272',
                    assetWidth: 1000,
                    assetHeight: 272,
                },
            },
            { label: 'Bishopi', compareSlug: 'bishopi' },
        ],
        greatText: 'A number for any domain: values, past sales, counts.',
        weakText: 'A number is not a decision about whether to buy at all.',
        iconName: 'chart-bar',
        accent: '#0e7490',
        accentSoft: '#cffafe',
        glow: 'rgba(34, 211, 238, 0.2)',
    },
    {
        title: 'AI naming tools',
        tools: [
            {
                label: 'ChatGPT',
                logo: {
                    src: '/public/tool-logos/chatgpt.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'Namelix',
                logo: {
                    src: '/public/tool-logos/namelix.svg',
                    width: 76 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 110 28',
                    assetWidth: 110,
                    assetHeight: 28,
                },
            },
        ],
        greatText: 'Endless ideas.',
        weakText: 'No idea what is actually for sale, at what price, or at what risk.',
        iconName: 'sparkles',
        accent: '#c2416c',
        accentSoft: '#ffe4e6',
        glow: 'rgba(251, 113, 133, 0.22)',
    },
    {
        // weakText source: content/atom.ts and content/brandbucket.ts (neutral evaluation rows).
        title: 'Marketplaces & auctions',
        tools: [
            {
                label: 'Sedo',
                logo: {
                    src: '/public/tool-logos/sedo-wordmark.webp',
                    width: 51,
                    height: 17,
                    viewBox: '0 0 92 24',
                    assetWidth: 92,
                    assetHeight: 24,
                },
            },
            {
                label: 'Atom',
                compareSlug: 'atom',
                logo: {
                    src: '/public/tool-logos/atom.svg',
                    width: 52,
                    height: 18,
                    viewBox: '0 0 84 24',
                    assetWidth: 84,
                    assetHeight: 24,
                },
            },
            {
                label: 'BrandBucket',
                compareSlug: 'brandbucket',
                logo: {
                    src: '/public/assets/compare/brandbucket/logo.png',
                    width: 63,
                    height: 17,
                    viewBox: '0 0 260 70',
                    assetWidth: 260,
                    assetHeight: 70,
                },
            },
            { label: 'GoDaddy Auctions' },
        ],
        greatText: 'Live inventory, ready to buy.',
        weakText: 'No neutral read on whether the name is worth the ask.',
        iconName: 'chart-bar',
        accent: '#315fa8',
        accentSoft: '#dbeafe',
        glow: 'rgba(96, 165, 250, 0.24)',
    },
    {
        // weakText source: content/instant-domain-search.ts ("it cannot tell you which name deserves the money").
        title: 'Registrars & instant checks',
        tools: [
            {
                label: 'Namecheap',
                logo: {
                    src: '/public/tool-logos/namecheap.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'GoDaddy',
                logo: {
                    src: '/public/tool-logos/godaddy.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'Porkbun',
                logo: {
                    src: '/public/tool-logos/porkbun.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'Instant Domain Search',
                compareSlug: 'instant-domain-search',
                logo: {
                    src: '/public/assets/compare/instant-domain-search/logo.svg',
                    width: 23,
                    height: 30,
                    viewBox: '0 0 564 747',
                    assetWidth: 564,
                    assetHeight: 747,
                },
            },
        ],
        greatText: 'Availability in milliseconds, checkout in minutes.',
        weakText: 'No help deciding which name deserves the money.',
        iconName: 'lock',
        accent: '#a15d1a',
        accentSoft: '#fef3c7',
        glow: 'rgba(251, 191, 36, 0.22)',
    },
    {
        title: 'Sheets, docs, and saved tabs',
        tools: [
            {
                label: 'Google Sheets',
                logo: {
                    src: '/public/tool-logos/googlesheets.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'Notion',
                logo: {
                    src: '/public/tool-logos/notion.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'Airtable',
                logo: {
                    src: '/public/tool-logos/airtable.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
            {
                label: 'Excel',
                logo: {
                    src: '/public/tool-logos/excel.svg',
                    width: 20 * DIFFERENTIATION_LOGO_SCALE,
                    height: 20 * DIFFERENTIATION_LOGO_SCALE,
                    viewBox: '0 0 24 24',
                    assetWidth: 24,
                    assetHeight: 24,
                },
            },
        ],
        greatText: 'All your candidates in one grid.',
        weakText: 'Frozen on paste day. Prices move, your sheet does not.',
        iconName: 'columns',
        accent: '#0f766e',
        accentSoft: '#ccfbf1',
        glow: 'rgba(45, 212, 191, 0.22)',
    },
] as const;
const getLandingCardStyle = (card: LandingCardVisual): React.CSSProperties =>
    ({
        '--ud-product-accent': card.accent,
        '--ud-product-accent-soft': card.accentSoft,
        '--ud-product-glow': card.glow,
    }) as React.CSSProperties;

const buildProductSearchHref = (prompt: string) => {
    const normalizedPrompt = prompt.trim();
    if (!normalizedPrompt) return encodeRadarFiltersUrl({}, { surface: 'product' });

    return encodeRadarFiltersUrl(
        {
            prompt: normalizedPrompt,
        },
        { surface: 'product' },
    );
};

const buildFinalPersonaAuthHref = (prompt: string, persona: ContextualSearchVisiblePersonaIntent): string => {
    return buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        persona,
        /* Built only by the final persona cards, which are an explicit founder-or-investor
           choice, so the declaration travels with its source. */
        personaSource: 'chosen',
        restart: true,
        /* The prompt-seeded search is a real destination now that the builder carries one,
           so the visitor lands on it signed in instead of on the bare Screener. */
        returnTo: toAbsoluteProductUrl(buildProductSearchHref(prompt)),
        feature: 'databaseAccess',
        action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
        from: 'website_landing_final_cta',
    });
};

const LandingExperience = ({ initialData }: LandingExperienceProps) => {
    const { enforceDomainPublicationLinks, renderedDomainResourcePaths } = useContext();
    const publishedDomainPaths = enforceDomainPublicationLinks
        ? renderedDomainResourcePaths
        : undefined;
    const resolvedPrompt = LANDING_PERSONAS[0].defaultPrompt;
    const workflowProductHref = initialData.links.productBaseUrl;
    const finalSignInHref = React.useMemo(
        () => buildWebsiteAuthHref({
            flow: 'cold',
            mode: 'login',
            returnTo: toAbsoluteProductUrl(buildProductSearchHref(resolvedPrompt)),
            feature: 'databaseAccess',
            action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
            from: 'website_landing_final_cta',
        }),
        [resolvedPrompt],
    );
    const homeGeoContext = React.useMemo(() => buildHomeGeoContext(), []);
    const homeHeroAskAIContext = React.useMemo<AskYourAIProps>(
        () => ({
            pageType: 'home',
            intent: 'evaluate_product',
            pageUrl: LANDING_WEBSITE_PAGE_ROUTES.home,
            pageTitle: 'Unique Domains homepage',
            geoContext: homeGeoContext,
            cta: 'open_database',
        }),
        [homeGeoContext],
    );
    // The final CTA headline counts the same indexed universe as the stats row above it,
    // so both read from `totalDomains` instead of restating a number that can go stale.
    const finalCtaHeadlineCount = React.useMemo(
        () => formatMillionsStat(initialData.stats.totalDomains) ?? HOME_FINAL_CTA_COPY.headlineCountFallback,
        [initialData.stats.totalDomains],
    );
    /**
     * The three figures under the hero. The domains and TLDs figures link to the listings they
     * count; the freshness figure has no page of its own since the data page was removed on
     * 2026-10-04, so it is a plain figure rather than a link that lands nowhere useful.
     *
     * A figure with no measurement behind it is omitted rather than shown as zero: the tiles
     * promise measured counts.
     */
    const heroStats = React.useMemo(
        () =>
            [
                {
                    id: 'domains',
                    value: initialData.stats.totalDomains,
                    label: 'domains',
                    href: WEBSITE_DOMAINS_BASE_PATH,
                    title: 'Browse the domains',
                },
                {
                    id: 'tlds',
                    value: initialData.stats.totalTlds,
                    label: 'TLDs',
                    href: LANDING_WEBSITE_PAGE_ROUTES.tldHub,
                    title: 'See the tracked TLDs',
                },
                {
                    id: 'checked',
                    value: initialData.stats.checkedLast24h,
                    label: 'checked in 24 h',
                    href: null,
                    title: null,
                },
            ].filter((stat) => stat.value > 0),
        [initialData.stats.checkedLast24h, initialData.stats.totalDomains, initialData.stats.totalTlds],
    );

    const trackProductEntry = React.useCallback((triggerId: ConversionTriggerId, href: string, section: string) => {
        trackConversionTrigger(triggerId, {
            action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
            feature: 'databaseAccess',
            href,
            page: href,
            section,
        });
    }, []);
    const trackFinalPersonaEntry = React.useCallback(
        (persona: ContextualSearchVisiblePersonaIntent, label: string, href: string) => {
            trackLandingCta({
                page: 'landing_home',
                section: 'final_cta',
                label,
                kind: 'persona',
                href,
            });
            track('persona_card_cta_clicked', { persona, section: 'final_cta' });
            trackConversionTrigger(FINAL_PERSONA_TRIGGER_IDS[persona], {
                href,
                page: href,
                section: 'final_cta',
                context: { persona },
            });
        },
        [],
    );
    return (
        <div class="ud-landing-shell ud-landing-shell-home min-h-screen">

            <div class="relative z-10">
                <Header
                    links={initialData.links}
                    navigation={initialData.navigation}
                    currentPath={LANDING_WEBSITE_PAGE_ROUTES.home}
                    trackingPage="landing_home"
                />

                {/* `main` opens on the hero: the headline, the figure and the stats are the page's
                    primary content, and they used to sit outside every landmark. */}
                <main>
                    <section class={`relative w-full pb-0 pt-7 ${HERO_SECTION_DESKTOP_PADDING_CLASS}`}>
                        <div class={`relative z-10 ${PAGE_CONTAINER_CLASS}`}>
                            <div class="text-left lg:mx-auto lg:max-w-[960px] lg:text-center">
                                {/* "One-word domains." sets 8.33em wide at this weight and tracking, so the
                                    4.35rem cap renders it at the 580px measure of the paragraph below; 6.8vw
                                    reaches that cap at 1024px, so the two blocks align from lg up. */}
                                <h1 class="text-[42px] font-medium leading-[1.02] tracking-[-0.035em] text-slate-900 lg:text-[clamp(2.75rem,6.8vw,4.35rem)] lg:leading-[1]">
                                    <span class="block">
                                        <span class="ud-landing-headline-highlight">One-word</span> domains.
                                    </span>
                                    {/* 0.6215em renders this line at the width of "One-word domains." above
                                        it; both scale from the same clamped size, so the match holds. The
                                        phone sets 0.62em against its own fixed 42px for the same reason. */}
                                    <span class="mt-2.5 block text-[0.62em] leading-[1.15] text-[#666] lg:mt-3 lg:text-[0.6215em] lg:leading-[inherit]">
                                        Search them all. <span class="text-[#333]">Buy the right one.</span>
                                    </span>
                                </h1>

                                {/* 580px keeps the justified copy at three lines with a nearly full last
                                    line; 560px pushed "you." onto a line of its own. Justification starts at
                                    sm because the mobile measure is too narrow to set without word gaps. */}
                                <p class="mt-[18px] max-w-[580px] text-pretty text-[16.5px] leading-[1.6] text-slate-600 sm:text-justify lg:mx-auto lg:mt-6 lg:text-lg lg:leading-[1.65]">
                                    Search millions of one-word domains by prompt or filters. Vet any name in
                                    minutes, with price, demand, history, trademark risk, and appraisals. Save
                                    your search, and new matches and price drops find you.
                                </p>

                                {/* The pair renders two `CtaPill` anchors and takes no width prop, so the
                                    phone's full-bleed stack is expressed on the row that owns them. */}
                                <div class="mt-[26px] flex flex-col gap-2.5 [&>a]:w-full lg:mt-8 lg:flex-row lg:flex-wrap lg:items-center lg:justify-center lg:gap-3 lg:[&>a]:w-fit">
                                    <PersonaAuthCtaPair
                                        attribution="website_landing_hero"
                                        investorTriggerId="website_landing_hero_investor"
                                        founderTriggerId="website_landing_hero_founder"
                                        page="landing_home"
                                        section="hero"
                                        surface="hero"
                                    />
                                </div>

                                <HomeSocialProof totalSignups={initialData.stats.totalSignups} surface="hero" />
                            </div>
                        </div>
                    </section>

                    {/* Real rows follow the hero (ADR-0028 amendment, 2026-09-27). The screener film
                        sat here, showed made-up rows and was the phone LCP element; it moved under
                        the `/product/search` hero. An unready search hides the section, it never
                        falls back to fake rows. */}
                    {initialData.domainsPreview ? (
                        <section class={`${PAGE_CONTAINER_CLASS} pb-0 pt-10 lg:pb-3 lg:pt-12`}>
                            <HomeDomainsPreview preview={initialData.domainsPreview} />
                        </section>
                    ) : null}

                    <section class={`${PAGE_CONTAINER_CLASS} flex flex-col gap-3.5 pb-0 pt-7 lg:items-center lg:gap-[18px] lg:pb-10 lg:pt-6`}>
                        {/* Three tiles side by side on a phone, the wide centred band from `lg`.
                            The figures used to carry `ud-landing-hero-stat*` from shared.css, which
                            is unlayered and therefore beats any utility a phone rule could add, so
                            both sets of values are spelled out here instead. */}
                        <div class="grid grid-cols-3 gap-2 lg:flex lg:flex-wrap lg:items-start lg:justify-center lg:gap-x-24 lg:gap-y-6">
                            {heroStats.map((stat) =>
                                stat.href ? (
                                    <a
                                        key={stat.id}
                                        class={HERO_STAT_LINK_CLASS}
                                        href={stat.href}
                                        title={stat.title ?? undefined}
                                        data-testid={`landing-hero-stat-${stat.id}`}
                                        onClick={() =>
                                            trackLandingCta({
                                                page: 'home',
                                                section: 'hero_stats',
                                                label: stat.label,
                                                kind: 'link',
                                            })
                                        }
                                    >
                                        <div class={HERO_STAT_VALUE_CLASS}>{formatCompactStat(stat.value)}</div>
                                        <div class={HERO_STAT_LABEL_CLASS}>
                                            {stat.label}
                                            <Icon name="arrow-up-right" size={10} strokeWidth={2.6} />
                                        </div>
                                    </a>
                                ) : (
                                    <div key={stat.id} class={HERO_STAT_TILE_CLASS} data-testid={`landing-hero-stat-${stat.id}`}>
                                        <div class={HERO_STAT_VALUE_CLASS}>{formatCompactStat(stat.value)}</div>
                                        <div class={HERO_STAT_LABEL_CLASS}>{stat.label}</div>
                                    </div>
                                ),
                            )}
                        </div>
                    </section>

                    <div class="ud-landing-main-surface">
                        <section class={`${PAGE_CONTAINER_CLASS} pb-0 pt-16 lg:pb-20 lg:pt-14`}>
                            <div class="lg:text-center">
                                <h2 class="text-pretty text-[30px] font-medium leading-[1.08] tracking-[-0.03em] text-slate-950 lg:mx-auto lg:text-balance lg:text-[3rem]">
                                    Most tools help you look.
                                </h2>
                            </div>

                            {/* Recolors every tool mark to one slate tone: alpha from darkness, white backgrounds become transparent. */}
                            <svg aria-hidden="true" width="0" height="0" class="absolute" focusable="false">
                                <defs>
                                    <filter id="ud-logo-mono" color-interpolation-filters="sRGB">
                                        <feColorMatrix
                                            type="matrix"
                                            values="0 0 0 0 0.278  0 0 0 0 0.333  0 0 0 0 0.412  -0.4677 -1.5734 -0.1588 2.2 0"
                                        />
                                    </filter>
                                </defs>
                            </svg>
                            {/* The tint is a wrapper rather than a class on the row itself: the
                                row zeroes its own horizontal padding from `lg` so the phone's
                                edge bleed can exist, and the shell needs 12px of it back. */}
                            <div class="mt-8 lg:mt-10 lg:rounded-[2rem] lg:bg-[#f1ece6] lg:p-3">
                                <div class={`${SNAP_ROW_CLASS} lg:grid lg:grid-cols-3 lg:gap-3`}>
                                <GlossarySection>
                                {DIFFERENTIATION_CARDS.map((card) => (
                                    <article
                                        key={card.title}
                                        class={`${SNAP_ITEM_CLASS} ${TOOL_LANDSCAPE_CARD_CLASS}`}
                                        style={getLandingCardStyle(card)}
                                    >
                                        <div class={TOOL_LANDSCAPE_LOGO_ROW_CLASS}>
                                            {card.tools.map((tool) => {
                                                const compareHref = tool.compareSlug ? comparePagePath(tool.compareSlug) : null;
                                                const mark = tool.logo ? (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={tool.logo.width}
                                                        height={tool.logo.height}
                                                        viewBox={tool.logo.viewBox}
                                                        role="img"
                                                        aria-label={tool.label}
                                                        class="ud-landing-logo-mark"
                                                    >
                                                        <image
                                                            href={tool.logo.src}
                                                            width={tool.logo.assetWidth}
                                                            height={tool.logo.assetHeight}
                                                            preserveAspectRatio="xMidYMid meet"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <span class="ud-landing-logo-mark ud-landing-logo-text">{tool.label}</span>
                                                );

                                                if (!compareHref) {
                                                    return (
                                                        <span key={tool.label} class="ud-landing-logo-slot">
                                                            {mark}
                                                        </span>
                                                    );
                                                }

                                                return (
                                                    <a
                                                        key={tool.label}
                                                        href={compareHref}
                                                        class="ud-landing-logo-slot ud-landing-logo-link"
                                                        aria-label={`Compare Unique Domains with ${tool.label}`}
                                                        onClick={() => trackLandingCta({
                                                            page: 'landing_home',
                                                            section: 'tool_landscape',
                                                            label: tool.label,
                                                            kind: 'compare_link',
                                                            href: compareHref,
                                                        })}
                                                    >
                                                        {mark}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                        <h3 class="text-[17px] font-bold tracking-[-0.01em] text-slate-950 lg:text-[16.5px]">
                                            {card.title}
                                        </h3>
                                        <div class="mt-3 space-y-2.5 lg:mt-4 lg:space-y-3">
                                            <p class="flex items-start gap-[10px] text-[14.5px] leading-[1.55] text-slate-600">
                                                <span class="ud-landing-card-paragraph-icon is-plus" aria-hidden="true">
                                                    +
                                                </span>
                                                <span><GlossaryText>{card.greatText}</GlossaryText></span>
                                            </p>
                                            <p class="flex items-start gap-[10px] text-[14.5px] leading-[1.55] text-slate-600">
                                                <span
                                                    class="ud-landing-card-paragraph-icon is-minus"
                                                    aria-hidden="true"
                                                >
                                                    &minus;
                                                </span>
                                                <span><GlossaryText>{card.weakText}</GlossaryText></span>
                                            </p>
                                        </div>
                                    </article>
                                ))}
                                </GlossarySection>
                                </div>
                            </div>

                            <div class="mt-3 flex items-center justify-between gap-3 lg:mt-8 lg:justify-center">
                                <span class="flex-none whitespace-nowrap text-[12.5px] font-semibold text-[#66696b] lg:hidden">
                                    {DIFFERENTIATION_CARDS.length} categories · swipe
                                </span>
                                <WebsiteSupportingLink
                                    href={COMPARE_BASE_PATH}
                                    size="md"
                                    onClick={() => trackLandingCta({
                                        page: 'landing_home',
                                        section: 'tool_landscape',
                                        label: 'See all comparisons',
                                        kind: 'secondary',
                                        href: COMPARE_BASE_PATH,
                                    })}
                                >
                                    See all comparisons
                                </WebsiteSupportingLink>
                            </div>
                        </section>

                        <div class="ud-landing-section-deferred">
                            <HomeWorkflowBento
                                ctaHref={workflowProductHref}
                                onCtaClick={() => {
                                    trackLandingCta({
                                        page: 'landing_home',
                                        section: 'product_workflow',
                                        label: START_WITH_BRIEF_LABEL,
                                        kind: 'product',
                                        href: workflowProductHref,
                                        feature: 'databaseAccess',
                                        action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
                                    });
                                    track('cta_open_screener_click', { section: 'product_workflow' });
                                    trackProductEntry(
                                        'website_landing_product_workflow_open_database',
                                        workflowProductHref,
                                        'landing_home_workflow_bento',
                                    );
                                }}
                            />
                        </div>

                        <div class="ud-landing-section-deferred">
                            <HomePlatformSection
                                pricingHref={LANDING_WEBSITE_PAGE_ROUTES.pricing}
                                onCapabilityClick={(capability) => trackLandingCta({
                                    page: 'landing_home',
                                    section: 'platform_capabilities',
                                    label: capability.id,
                                    kind: 'feature',
                                    href: capability.href,
                                })}
                                onPricingClick={() => trackLandingCta({
                                    page: 'landing_home',
                                    section: 'platform_capabilities',
                                    label: 'See what it costs',
                                    kind: 'secondary',
                                    href: LANDING_WEBSITE_PAGE_ROUTES.pricing,
                                })}
                            />
                        </div>
                    </div>

                    <div class="ud-landing-section-deferred">
                        <FAQ
                            title="Questions people ask before they trust a domain tool."
                            description="Direct answers about one-word domains, what a report proves, and what it costs."
                            items={HOME_FAQ_ITEMS}
                            surface="panel"
                            sectionClassName={FAQ_SECTION_CLASS}
                            showItemLinks
                            isItemLinkVisible={(link) =>
                                isPublishedHumanPageTarget(link.href, publishedDomainPaths)
                            }
                        />
                    </div>

                    <section
                        id="solutions"
                        class={`ud-landing-section-deferred ${PAGE_CONTAINER_CLASS} scroll-mt-[calc(var(--ud-topbar-h,60px)+24px)] pt-[72px] lg:pt-32`}
                    >
                        <div
                            data-reveal
                            data-testid="landing-home-footer-product-entry-card"
                            class="ud-dark-block rounded-[32px] px-[22px] pb-[30px] pt-9 text-left shadow-[0_30px_70px_rgba(27,29,30,0.18)] lg:rounded-[36px] lg:px-14 lg:pb-[60px] lg:pt-[72px] lg:text-center"
                        >
                            <h2 class="text-pretty text-[30px] font-medium leading-[1.08] tracking-[-0.03em] text-white lg:mx-auto lg:max-w-[640px] lg:text-balance lg:text-[clamp(2.125rem,3.4vw,2.875rem)]">
                                {finalCtaHeadlineCount} {HOME_FINAL_CTA_COPY.headlineLead}
                                <br />
                                <span class="text-white/[0.68]">{HOME_FINAL_CTA_COPY.headlineFollowUp}</span>
                            </h2>
                            <p class="mt-4 text-[15.5px] leading-[1.6] text-white/65 lg:mx-auto lg:mt-[18px] lg:max-w-[540px] lg:text-base lg:leading-[1.65]">
                                {HOME_FINAL_CTA_COPY.subtitle}
                            </p>

                            <HomeSocialProof totalSignups={initialData.stats.totalSignups} surface="finalCta" />

                            <div class="mt-[26px] grid gap-3 lg:mx-auto lg:mt-9 lg:max-w-[860px] lg:grid-cols-2 lg:gap-[14px]">
                                {VISIBLE_CONTEXTUAL_SEARCH_PERSONAS.map((persona) => {
                                    const href = buildFinalPersonaAuthHref(resolvedPrompt, persona.id);
                                    const card = HOME_FINAL_CTA_PERSONA_CARDS[persona.id];

                                    // The whole card is the anchor, so it holds no nested link: the
                                    // informational /solutions destinations stay reachable from the
                                    // header Solutions menu on every page.
                                    // The focus ring reads --color-accent, the token declared in
                                    // tailwind.css. The two links replaced here asked for an
                                    // undeclared --ud-accent, so their ring painted no color at all.
                                    return (
                                        <a
                                            key={persona.id}
                                            href={href}
                                            data-testid="landing-home-footer-persona-cta"
                                            data-persona={persona.id}
                                            onClick={() => trackFinalPersonaEntry(persona.id, persona.title, href)}
                                            class="group flex flex-col gap-[15px] rounded-[20px] bg-[#FCFBF8] px-[26px] pb-5 pt-[26px] text-left shadow-[0_18px_44px_rgba(0,0,0,0.28)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_26px_54px_rgba(0,0,0,0.34)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#141516]"
                                        >
                                            <span class="flex items-center gap-[14px]">
                                                <span
                                                    class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-[#1b1d1e] text-[var(--color-accent)]"
                                                    aria-hidden="true"
                                                >
                                                    <Icon name={persona.icon} size={20} />
                                                </span>
                                                <span class="min-w-0">
                                                    <span class="block text-lg font-bold text-slate-900">{persona.title}</span>
                                                    <span class="mt-0.5 block text-[13.5px] leading-[1.45] text-slate-600">
                                                        {persona.description}
                                                    </span>
                                                </span>
                                            </span>

                                            <ul class="flex list-none flex-col gap-2 p-0">
                                                {card.bullets.map((bullet) => (
                                                    <li
                                                        key={bullet}
                                                        class="flex items-start gap-[9px] text-[13.5px] leading-[1.45] text-slate-700"
                                                    >
                                                        <Icon
                                                            name="check"
                                                            size={15}
                                                            className="mt-0.5 shrink-0 text-slate-900"
                                                            strokeWidth={2.5}
                                                        />
                                                        {bullet}
                                                    </li>
                                                ))}
                                            </ul>

                                            <span class="mt-0.5 flex items-center justify-between gap-3 border-t border-[var(--color-card-2)] pt-[14px]">
                                                <span class="text-[14.5px] font-bold text-slate-900">
                                                    {card.startLabel}
                                                </span>
                                                <span
                                                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-slate-900 transition duration-200 group-hover:translate-x-0.5"
                                                    aria-hidden="true"
                                                >
                                                    <Icon name="arrow-right" size={15} strokeWidth={2.2} />
                                                </span>
                                            </span>
                                        </a>
                                    );
                                })}
                            </div>

                            <div class="mt-[22px] flex flex-col items-start gap-2.5 lg:mt-[26px] lg:items-center">
                                <p class="text-[12.5px] leading-[1.5] text-white/55 lg:text-[13px]">
                                    {HOME_FINAL_CTA_COPY.reassurance.join('  ·  ')}
                                </p>
                                <a
                                    href={finalSignInHref}
                                    data-testid="landing-home-footer-sign-in"
                                    onClick={() => {
                                        trackLandingCta({
                                            page: 'landing_home',
                                            section: 'final_cta',
                                            label: HOME_FINAL_CTA_COPY.alreadyMemberCtaLabel,
                                            kind: 'auth',
                                            href: finalSignInHref,
                                        });
                                        trackConversionTrigger('website_landing_final_sign_in', {
                                            href: finalSignInHref,
                                            page: finalSignInHref,
                                            section: 'final_cta',
                                        });
                                    }}
                                    class="rounded-sm text-sm font-semibold text-white/70 underline decoration-white/30 underline-offset-4 transition duration-200 hover:text-white hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#141516]"
                                >
                                    {HOME_FINAL_CTA_COPY.alreadyMemberCtaLabel}
                                </a>
                            </div>
                        </div>
                    </section>
                </main>

                <div class="ud-landing-section-deferred">
                    <HomeAskAiSection context={homeHeroAskAIContext} />

                    <PageGeoBlock
                        className="pb-2 pt-8 [&_details]:border-t-0 [&_details]:pt-0"
                        geo={homeGeoContext}
                        surface="landing_home"
                    />

                    <div class={`${PAGE_CONTAINER_CLASS} pt-8`}>
                        <div class="h-px w-full bg-[var(--color-card-2)]" />
                    </div>

                    <Footer links={initialData.links} trackingPage="landing_home" />
                </div>
            </div>
        </div>
    );
};

export default LandingExperience;
